import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GaussianSplattingPointCloudElement } from '@thive/canvas';
import { Vector3 } from 'three';
import { Button, Divider, Stack, Typography } from '@mui/material';

import { PropertyPanelContainer } from '@common/components/PropertyPanelContainer';
import { logger } from '@/modules/common/logging/logger';
import { Accordion } from '@/modules/common/components/Accordion';
import { NumberInput } from '@/modules/common/components/inputs';
import { assertNever } from '@/helpers/utils';
import { useAppStore } from '@/modules/app/store/useAppStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { POINTCLOUD_DEFAULT } from '@/modules/pointCloud';
import { ReferenceImageServiceFactory } from '@/modules/referenceImage/service/ReferenceImageServiceFactory';

import { useFleetTrackerCanvasStore } from '../store/useFleetTrackerCanvasStore';
import { UserPreferenceName } from '@/modules/userPreferences';

export function PropertyPanel() {
  const activeModule = useAppStore((state) => state.activeModule);
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const referenceImageSvc = useMemo(
    () => ReferenceImageServiceFactory.getInstance().createService(),
    [],
  );
  const navigate = useNavigate();

  const [pointCloudState, setPointCloudState] = useState<{
    position: { x: number; y: number; z: number };
    rotation: { x: number; y: number; z: number };
    scale: { x: number; y: number; z: number };
  }>({
    position: {
      x: POINTCLOUD_DEFAULT.position.x,
      y: POINTCLOUD_DEFAULT.position.y,
      z: POINTCLOUD_DEFAULT.position.z,
    },
    rotation: {
      x: POINTCLOUD_DEFAULT.rotation.x,
      y: POINTCLOUD_DEFAULT.rotation.y,
      z: POINTCLOUD_DEFAULT.rotation.z,
    },
    scale: {
      x: POINTCLOUD_DEFAULT.scale.x,
      y: POINTCLOUD_DEFAULT.scale.y,
      z: POINTCLOUD_DEFAULT.scale.z,
    },
  });

  const panelTitle =
    activeModule === 'visualization'
      ? 'interface:tabs.visualization.label'
      : 'interface:tabs.viewer.label';

  const onCloseClick = useCallback(() => {
    const { projectId, alternativeId, revisionId } = useFloorplanDesignStore.getState().metaData;
    const targetLayoutPath = `/${projectId}/builder/${alternativeId}/version/${revisionId}`;

    switch (activeModule) {
      case 'visualization':
        referenceImageSvc.loadReferenceImage(referenceImageSvc.getReferenceImage());
        appSvc.goToModule('canvas_simulation');
        navigate(targetLayoutPath);
        return;
      case 'canvas_viewer':
        appSvc.goToModule('canvas_layout');
        navigate(targetLayoutPath);
        return;
      case 'canvas_setup':
      case 'canvas_layout':
      case 'canvas_simulation':
        appSvc.goToModule('canvas_layout');
        return;
      default:
        assertNever(activeModule);
    }
  }, [activeModule, appSvc, navigate, referenceImageSvc]);

  const applyAdjustments = useCallback(() => {
    const { canvasRef: canvas } = useFleetTrackerCanvasStore.getState();
    if (!canvas) {
      logger.error('Expected canvas to be defined');
      return;
    }

    const pcElement = canvas.getElement('pointcloud');

    if (!pcElement) {
      logger.error('Could not find pointcloud element in canvas');
      return;
    }

    const gausElement: GaussianSplattingPointCloudElement = {
      ...(pcElement as GaussianSplattingPointCloudElement),
      position: new Vector3(
        pointCloudState.position.x,
        pointCloudState.position.y,
        pointCloudState.position.z,
      ),
      rotation: new Vector3(
        pointCloudState.rotation.x,
        pointCloudState.rotation.y,
        pointCloudState.rotation.z,
      ),
      scale: new Vector3(pointCloudState.scale.x, pointCloudState.scale.y, pointCloudState.scale.z),
    };

    canvas.updateElements([gausElement]);
  }, [pointCloudState]);

  const handleChange = useCallback(
    (field: 'posX' | 'posY' | 'posZ' | 'rotX' | 'rotY' | 'rotZ' | 'scaleX' | 'scaleY' | 'scaleZ') =>
      (value: number) => {
        switch (field) {
          case 'posX':
            setPointCloudState((current) => ({
              ...current,
              position: {
                ...current.position,
                x: value,
              },
            }));

            return;
          case 'posY':
            setPointCloudState((current) => ({
              ...current,
              position: {
                ...current.position,
                y: value,
              },
            }));

            return;
          case 'posZ':
            setPointCloudState((current) => ({
              ...current,
              position: {
                ...current.position,
                z: value,
              },
            }));

            return;
          case 'rotX':
            setPointCloudState((current) => ({
              ...current,
              rotation: {
                ...current.rotation,
                x: value,
              },
            }));

            return;
          case 'rotY':
            setPointCloudState((current) => ({
              ...current,
              rotation: {
                ...current.rotation,
                y: value,
              },
            }));

            return;
          case 'rotZ':
            setPointCloudState((current) => ({
              ...current,
              rotation: {
                ...current.rotation,
                z: value,
              },
            }));

            return;
          case 'scaleX':
            setPointCloudState((current) => ({
              ...current,
              scale: {
                ...current.scale,
                x: value,
              },
            }));

            return;
          case 'scaleY':
            setPointCloudState((current) => ({
              ...current,
              scale: {
                ...current.scale,
                y: value,
              },
            }));

            return;
          case 'scaleZ':
            setPointCloudState((current) => ({
              ...current,
              scale: {
                ...current.scale,
                z: value,
              },
            }));

            return;
          default:
            assertNever(field);
        }
      },
    [],
  );

  const showPointCloudAdjustments = useMemo(
    () => useFloorplanDesignStore.getState().uxonly.pointCloudOutputFileRef !== undefined,
    [],
  );

  return (
    <PropertyPanelContainer labelKey={panelTitle} onCloseClick={onCloseClick}>
      <div />
      {showPointCloudAdjustments && (
        <Accordion
          persistId={UserPreferenceName.ACCORDION_VISUALIZATION_DEMO}
          title='Demo'
          unmountOnExit
          sx={{ px: 2 }}
        >
          <Stack direction='column'>
            <Typography>adjust pointcloud</Typography>
            <Stack>position</Stack>
            <Stack>
              <Typography>x: </Typography>
              <NumberInput
                value={pointCloudState.position.x}
                onChange={handleChange('posX')}
                disabled={false}
              />
            </Stack>
            <Stack>
              <Typography>y: </Typography>
              <NumberInput
                value={pointCloudState.position.y}
                onChange={handleChange('posY')}
                disabled={false}
              />
            </Stack>
            <Stack>
              <Typography>z: </Typography>
              <NumberInput
                value={pointCloudState.position.z}
                onChange={handleChange('posZ')}
                disabled={false}
              />
            </Stack>
            <Divider />

            <Stack>rotation</Stack>
            <Stack>
              <Typography>x: </Typography>
              <NumberInput
                value={pointCloudState.rotation.x}
                onChange={handleChange('rotX')}
                disabled={false}
              />
            </Stack>
            <Stack>
              <Typography>y: </Typography>
              <NumberInput
                value={pointCloudState.rotation.y}
                onChange={handleChange('rotY')}
                disabled={false}
              />
            </Stack>
            <Stack>
              <Typography>z: </Typography>
              <NumberInput
                value={pointCloudState.rotation.z}
                onChange={handleChange('rotZ')}
                disabled={false}
              />
            </Stack>
            <Divider />

            <Stack>scale</Stack>
            <Stack>
              <Typography>x: </Typography>
              <NumberInput
                value={pointCloudState.scale.x}
                onChange={handleChange('scaleX')}
                disabled={false}
              />
            </Stack>
            <Stack>
              <Typography>y: </Typography>
              <NumberInput
                value={pointCloudState.scale.y}
                onChange={handleChange('scaleY')}
                disabled={false}
              />
            </Stack>
            <Stack>
              <Typography>z: </Typography>
              <NumberInput
                value={pointCloudState.scale.z}
                onChange={handleChange('scaleZ')}
                disabled={false}
              />
            </Stack>

            <Button onClick={applyAdjustments}>apply changes</Button>
          </Stack>
        </Accordion>
      )}
    </PropertyPanelContainer>
  );
}
